<template>
  <app-page
    :title="pageTitle"
    :subtitle="pageSubtitle"
    :back-route="backRoute"
    :previous="[{ label: 'Recognition', to: { name: 'recognition' } }]"
  >
    <template #extraTitle>
      <active-company-pill class="ml-2" />
    </template>

    <template v-if="!loading && mode === 'edit'" #below-header>
      <b-form-group label="Group name" label-for="award-group-name" class="award-group-editor-page__name-field">
        <b-form-input id="award-group-name" v-model="groupName" placeholder="Group name" />
      </b-form-group>
    </template>

    <template v-if="!loading && mode === 'edit' && combinedBudgetMinor > 0" #header-content>
      <app-stat
        class="budget-stat"
        title="Annual budget"
        :stat="budgetRemainingMinor"
        :formatter="formatBudget"
        subtitle="remaining"
        small
      >
        <b-progress :value="totalSpentMinor" :max="combinedBudgetMinor" class="budget-progress mt-2 mb-2" />
        <div class="budget-footer">
          <span
            ><strong>{{ formatBudget(combinedBudgetMinor) }}</strong> Budget</span
          >
          <span :class="budgetSpentClass"
            ><strong>{{ formatBudget(totalSpentMinor) }}</strong> Spent</span
          >
        </div>
      </app-stat>
    </template>

    <template #custom-content>
      <b-col>
        <button
          v-if="view === 'review'"
          type="button"
          class="award-group-editor-page__back-to-editor"
          @click="backToEditor"
        >
          <font-awesome-icon :icon="['fas', 'arrow-left']" class="mr-2" />Back to editor
        </button>

        <div v-if="loading" class="text-center py-5">
          <b-spinner />
        </div>

        <award-group-editor
          v-if="view === 'edit' && programType && !loading"
          :mode="mode"
          :program-type="programType"
          :rows="rows"
          :row-template="rowTemplate"
          :division-options="divisionOptions"
          :employee-options="employeeOptions"
          :show-errors="showErrors"
          @input="rows = $event"
          @validation="validation = $event"
        />

        <award-group-review
          v-else-if="view === 'review'"
          :rows="rows"
          :division-options="divisionOptions"
          :employee-options="employeeOptions"
          :balance-minor="balanceMinor"
          :combined-budget-minor="combinedBudgetMinor"
          :group-name="destGroupName"
          @toggle-active="onReviewToggleActive"
        />
      </b-col>
    </template>

    <template #footer>
      <div v-if="view === 'review'" class="d-flex align-items-center justify-content-between w-100 px-4 py-3">
        <b-button variant="outline-secondary" :disabled="saving" @click="onCancel">Cancel</b-button>
        <div class="d-flex align-items-center">
          <small v-if="errorMessage" class="text-danger mr-3">{{ errorMessage }}</small>
          <b-button variant="primary" :disabled="saving" @click="onSubmit">
            <b-spinner v-if="saving" class="mr-2" small />
            {{ submitLabel }}
          </b-button>
        </div>
      </div>

      <div v-else class="d-flex align-items-center justify-content-between w-100 px-4 py-3">
        <b-button v-if="mode === 'create'" variant="outline-secondary" :disabled="saving" @click="onCancel">
          Cancel
        </b-button>
        <span v-else></span>
        <div class="d-flex align-items-center">
          <small v-if="errorMessage" class="text-danger mr-3">{{ errorMessage }}</small>
          <b-button
            v-if="mode === 'edit'"
            variant="outline-secondary"
            class="mr-2"
            :disabled="saving || !isDirty"
            @click="resetForm"
          >
            Reset
          </b-button>
          <b-button
            variant="primary"
            :disabled="saving || loading || (mode === 'edit' && !isDirty) || (mode === 'create' && rows.length === 0)"
            @click="onPrimaryAction"
          >
            <b-spinner v-if="saving" class="mr-2" small />
            {{ submitLabel }}
          </b-button>
        </div>
      </div>
    </template>
  </app-page>
</template>

<script setup>
import ActiveCompanyPill from '@/pages/recognition/components/ActiveCompanyPill.vue';
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router/composables';
import ApiClient from '@/ApiClient';
import AppPage from '@/components/AppPage.vue';
import AppStat from '@/components/AppStat.vue';
import AwardGroupEditor from '@/pages/recognition/components/AwardGroupEditor.vue';
import AwardGroupReview from '@/pages/recognition/components/AwardGroupReview.vue';
import { programToRow, rowToCreateMember, rowToUpdateMember } from '@/pages/recognition/composables/useAwardRowModel';
import useToast from '@/composables/useToast';
import { toCurrency } from '@/utils';
import State from '@/state/State';

const props = defineProps({
  sourceId: { type: String, default: null },
  groupId: { type: String, default: null },
  copies: { type: Number, default: 1 },
});

const $router = useRouter();
const { doToast, TOAST_VARIANT } = useToast();

const mode = computed(() => (props.groupId ? 'edit' : 'create'));
const loading = ref(true);
const saving = ref(false);
const errorMessage = ref('');
const rows = ref([]);
const groupName = ref('');
const programType = ref(null);
const sourceProgramId = ref(null);
const divisionOptions = ref([]);
const employeeOptions = ref([]);
// Original award as an editor row — used to seed "Add award" after the table is emptied.
const rowTemplate = ref(null);
// Required-field validation, surfaced by the editor. showErrors flips on after a
// failed submit so empty required cells outline red.
const validation = ref({ count: 0, columns: [] });
const showErrors = ref(false);
const validationMessage = computed(() => {
  const { count, columns } = validation.value;
  if (!count) {
    return '';
  }
  const names = columns.map(label => label.toLowerCase()).join(', ');
  return `${count} ${count === 1 ? 'field' : 'fields'} not populated in ${names}`;
});
// Create mode steps through edit -> review. The destination is derived from the
// source award: its existing group, or a new group named after it.
const view = ref('edit');
const balanceMinor = ref(null);
// {type, recognition_award_group_id, groupName, includeSource} resolved in initCreate.
const destinationSelection = ref(null);

const effectiveDestination = computed(() => destinationSelection.value?.type || 'new_group');
const includeSourceEffective = computed(() => destinationSelection.value?.includeSource ?? true);
const destGroupName = computed(() => destinationSelection.value?.groupName || groupName.value);

// Top-left back arrow: edit an existing group -> recognition; setting up new awards
// -> back to the duplicate picker. On review we show a "Back to editor" button instead.
const backRoute = computed(() => {
  if (view.value === 'review') {
    return undefined;
  }
  return mode.value === 'edit' ? '/recognition' : '/recognition/duplicate';
});

const submitLabel = computed(() => {
  if (saving.value) {
    return mode.value === 'edit' ? 'Saving...' : 'Creating...';
  }
  if (mode.value === 'edit') {
    return 'Save changes';
  }
  const n = rows.value.length;
  if (n === 0) {
    return 'Create award';
  }
  return `Create ${n} ${n === 1 ? 'award' : 'awards'}`;
});

// Normalise each award's budget to a yearly figure before summing so the review's
// "combined annual budget" is truly annual (monthly counts 12x, quarterly 4x). A budget
// with no period can't reach here — validation blocks it before review/submit.
const BUDGET_ANNUAL_FACTOR = { YEAR: 1, QUARTER: 4, MONTH: 12 };
const combinedBudgetMinor = computed(() =>
  rows.value.reduce((sum, row) => {
    if (!row.monetary_enabled || !row.budget_minor) {
      return sum;
    }
    return sum + row.budget_minor * (BUDGET_ANNUAL_FACTOR[row.budget_cadence] ?? 1);
  }, 0),
);

// Annual budget card (mirrors the single-award Budget card, summed across the group).
const currency = computed(() => State.state?.company?.default_currency);
const totalSpentMinor = computed(() =>
  rows.value.reduce((sum, row) => {
    if (!row.monetary_enabled || !row.budget_minor) {
      return sum;
    }
    // budget_spent_minor is per-period spend; annualise it the same way combinedBudgetMinor
    // annualises budget_minor so the progress bar and "remaining" compare like for like.
    return sum + (row.budget_spent_minor || 0) * (BUDGET_ANNUAL_FACTOR[row.budget_cadence] ?? 1);
  }, 0),
);
const budgetRemainingMinor = computed(() => combinedBudgetMinor.value - totalSpentMinor.value);
const budgetSpentClass = computed(() => {
  const budget = combinedBudgetMinor.value;
  if (!budget) {
    return 'budget-footer__spent--success';
  }
  if (totalSpentMinor.value > budget) {
    return 'budget-footer__spent--danger';
  }
  if (totalSpentMinor.value >= budget * 0.9) {
    return 'budget-footer__spent--warning';
  }
  return 'budget-footer__spent--success';
});
const formatBudget = value => toCurrency(value, currency.value).replace(/\.00$/, '');

const pageTitle = computed(() => {
  if (mode.value === 'edit') {
    return 'Edit award group';
  }
  if (view.value === 'review') {
    const n = rows.value.length;
    return `You're about to create ${n} ${n === 1 ? 'award' : 'awards'}`;
  }
  return 'Set up your awards';
});

const pageSubtitle = computed(() => {
  if (mode.value === 'edit') {
    return 'Edit any of the awards in this group and save your changes in one go.';
  }
  if (view.value === 'review') {
    const tail = ' and go live immediately. You can edit or remove any of them afterwards.';
    if (effectiveDestination.value === 'existing_group') {
      return `They'll be added to "${destGroupName.value}"${tail}`;
    }
    return `They'll be grouped as "${destGroupName.value}"${tail}`;
  }
  return (
    'Each award below is a copy of the original — change whatever differs between them, then create them all at once. ' +
    'Tick the checkboxes to edit several awards together, and pin a column (e.g. award name or description) to keep it ' +
    'visible as a reference while you edit the others. Once created, awards can be edited individually or from the group editor.'
  );
});

function onReviewToggleActive(index) {
  const next = rows.value.slice();
  if (next[index]) {
    next[index] = { ...next[index], is_active: !next[index].is_active };
    rows.value = next;
  }
}

async function loadOptions() {
  try {
    const divisions = (await ApiClient.getDivisions())?.data || [];
    divisionOptions.value = divisions.map(d => ({ text: d.name, value: String(d.division_id) }));
  } catch (e) {
    divisionOptions.value = [];
  }
  try {
    const data = await ApiClient.listRecognitionEmployees();
    const list = Array.isArray(data) ? data : data.employees || data.data || [];
    employeeOptions.value = list.map(e => ({
      text: e.work_email ? `${e.full_name} - ${e.work_email}` : e.full_name,
      value: String(e.employee_id),
    }));
  } catch (e) {
    employeeOptions.value = [];
  }
}

async function initCreate() {
  const source = await ApiClient.getRecognitionProgram(props.sourceId);
  sourceProgramId.value = source.recognition_award_program_id;
  programType.value = source.program_type;
  // A copy has none of the source's award history, so nothing on it is locked.
  const seed = { ...programToRow(source), recognition_award_program_id: null, has_been_awarded: false };
  rowTemplate.value = seed;
  const count = Math.max(1, props.copies || 1);
  rows.value = Array.from({ length: count }, () => ({ ...seed }));

  // Destination is automatic: if the source already belongs to a group, the new
  // awards join it; otherwise they form a new group named after the source award.
  if (source.recognition_award_group_id) {
    let existingName = '';
    try {
      const group = await ApiClient.getRecognitionAwardGroup(source.recognition_award_group_id);
      existingName = group?.name || '';
    } catch (e) {
      existingName = '';
    }
    destinationSelection.value = {
      type: 'existing_group',
      recognition_award_group_id: String(source.recognition_award_group_id),
      groupName: existingName,
      includeSource: false,
    };
  } else {
    destinationSelection.value = {
      type: 'new_group',
      groupName: source.program_name || '',
      includeSource: true,
    };
  }
}

async function initEdit() {
  const group = await ApiClient.getRecognitionAwardGroup(props.groupId);
  groupName.value = group.name;
  const members = group.members || [];
  programType.value = members.length ? members[0].program_type : null;
  rows.value = members.map(programToRow);
}

async function loadBalance() {
  try {
    const ledger = await ApiClient.getRecognitionLedger();
    if (ledger && typeof ledger.balance_minor === 'number') {
      balanceMinor.value = ledger.balance_minor;
    }
  } catch (e) {
    balanceMinor.value = null;
  }
}

// Snapshot of the loaded state, used to detect edits and to reset back to it.
const originalState = ref('');
function snapshot() {
  return JSON.stringify({ rows: rows.value, groupName: groupName.value });
}
const isDirty = computed(() => originalState.value !== snapshot());

function resetForm() {
  const original = JSON.parse(originalState.value);
  rows.value = original.rows;
  groupName.value = original.groupName;
  errorMessage.value = '';
}

onMounted(async () => {
  await loadOptions();
  try {
    if (mode.value === 'edit') {
      await initEdit();
    } else {
      await initCreate();
      await loadBalance();
    }
    originalState.value = snapshot();
  } catch (e) {
    errorMessage.value = 'Could not load awards. Please try again.';
  } finally {
    loading.value = false;
  }
});

function goToReview() {
  if (!validate()) {
    return;
  }
  view.value = 'review';
}

function backToEditor() {
  view.value = 'edit';
}

function onCancel() {
  $router.push({ name: 'recognition' });
}

function onPrimaryAction() {
  if (mode.value === 'edit') {
    onSubmit();
  } else {
    goToReview();
  }
}

function buildCreatedMessage() {
  const n = rows.value.length;
  const awardWord = n === 1 ? 'award' : 'awards';
  const groupKind = effectiveDestination.value === 'existing_group' ? 'existing' : 'new';
  return `${n} ${awardWord} created in ${groupKind} group "${destGroupName.value}".`;
}

function buildDestination() {
  if (effectiveDestination.value === 'existing_group') {
    return {
      type: 'existing_group',
      recognition_award_group_id: destinationSelection.value?.recognition_award_group_id,
      include_source: includeSourceEffective.value,
    };
  }
  return { type: 'new_group', name: destGroupName.value, include_source: includeSourceEffective.value };
}

function validate() {
  // Edit mode renames the group here; create mode names the group in the destination step.
  if (mode.value === 'edit' && (!groupName.value || !groupName.value.trim())) {
    errorMessage.value = 'Group name is required.';
    return false;
  }
  if (validation.value.count > 0) {
    showErrors.value = true;
    errorMessage.value = validationMessage.value;
    return false;
  }
  errorMessage.value = '';
  return true;
}

// Once errors are shown, keep the message in sync as the user fills the fields in.
watch(validationMessage, message => {
  if (showErrors.value) {
    errorMessage.value = message;
  }
});

async function onSubmit() {
  if (!validate()) {
    return;
  }
  saving.value = true;
  try {
    if (mode.value === 'edit') {
      await ApiClient.renameRecognitionAwardGroup(props.groupId, groupName.value);
      await ApiClient.bulkUpdateRecognitionAwardGroupMembers(props.groupId, rows.value.map(rowToUpdateMember));
      doToast('Success', `Award group "${groupName.value}" updated.`, TOAST_VARIANT.SUCCESS);
    } else {
      await ApiClient.duplicateRecognitionAwards({
        source_program_id: sourceProgramId.value,
        members: rows.value.map(rowToCreateMember),
        destination: buildDestination(),
      });
      doToast('Success', buildCreatedMessage(), TOAST_VARIANT.SUCCESS);
    }
    $router.push({ name: 'recognition' });
  } catch (e) {
    // "Try again" is wrong advice for a conflict, so prefer the API's reason.
    const message = e?.response?.data?.message || 'Something went wrong saving the awards. Please try again.';
    errorMessage.value = message;
    doToast('Error', message, TOAST_VARIANT.DANGER);
    saving.value = false;
  }
}
</script>

<style scoped lang="scss">
// Matches the "Add award" button styling in AwardGroupEditor.
.award-group-editor-page__back-to-editor {
  display: inline-flex;
  align-items: center;
  border: none;
  background: none;
  padding: 0.75rem 0.25rem;
  margin-bottom: 0.25rem;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--palette-color-brand-primary);

  &:hover {
    text-decoration: underline;
  }
}

.award-group-editor-page__name-field {
  max-width: 24rem;
  margin-top: 1.5rem;
  margin-bottom: 0;
}

// Top-align the header row so the Annual budget card's top lines up with the
// "Edit award group" title (same as the single-award view). AppPage's row uses the
// Bootstrap .align-items-end utility (!important), so override in kind.
:deep(.page-content > .d-flex.justify-content-between.align-items-end) {
  align-items: flex-start !important;
}

// Annual budget card — mirrors the single-award Budget card in AwardDetail.
.budget-stat {
  min-width: 17.5rem;

  :deep(.app-stat__title),
  :deep(.app-stat__value) {
    color: var(--palette-color-default);
  }

  :deep(.app-stat__subtitle) {
    color: var(--palette-color-default-lighten-30);
  }
}

.budget-progress {
  height: 0.5rem;
  border-radius: 0.25rem;
  background-color: var(--palette-color-core-1-lighten-80);

  :deep(.progress-bar) {
    background-color: var(--palette-color-core-1);
  }
}

.budget-footer {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: var(--palette-color-default-lighten-30);

  strong {
    color: var(--palette-color-default);
  }

  &__spent--success strong {
    color: var(--palette-color-success);
  }

  &__spent--warning strong {
    color: var(--palette-color-warning);
  }

  &__spent--danger strong {
    color: var(--palette-color-danger);
  }
}
</style>
